// ReferenceSection.tsx
import React, { useState, useEffect } from 'react';
import { FreshIcon, Cryopreserve, PoolIcon } from "../../../assets/CommonSvg"
import { Button, Tooltip, CircularProgress } from '@mui/material';
import { actionButtons } from '../../../../bmr/constants/constant'
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentCycle } from "../../../slices/common"
import { setSubjectDataList } from "../../../slices/help"
import { approveChangeRequest, rejectChangeRequest } from "../../../slices/manufacturer"
import { hasAuthority } from '../../../../utils/authorities'
import { selectV2VCycle } from "../../../slices/common";
import { getTooltipTextButton, hasCRaisePermission, hasCRPermission } from "../../../../utils/utils"
import BsModal from "../../common/BaseModal"
import TemplateForm from "./TemplateForm"
import RequestHistory from '../../../../bmr/pages/appointment-details/RequestHistory'
import bmrSubjectApi from '../../../../bmr/services/bmrSubjectService';
import CR from './CR'

const TERMINATE_ACTION = "change-terminate-cycle-status";

const ReferenceSection = ({
  className = "",
  onUpdate = {},
  subject_id = '',
  actualSteps = []
}) => {
  const dispatch = useDispatch();
  const V2VCycle = useSelector(selectV2VCycle);
  const LoginData = useSelector((state) => state.loginData);
  const bmr_permissions = LoginData?.bmr_permissions || []
  const cr_permissions = LoginData?.cr_permissions || [];
  const [openModal, setOpenModal] = useState(false);
  const [SubjectData, setSubjectData] = useState({});
  const [slug, setSlug] = useState('');
  const [showManufectureButton, setShowManufectureButton] = useState(false)
  const currentCycle = useSelector(selectCurrentCycle)
  const [cycleList, setCycleList] = useState([])
  const [loadingButton, setLoadingButton] = useState(null); // Track which button is loading

  useEffect(() => {
    const data = V2VCycle?.filter((list) => list.cycle != currentCycle?.cycle && list.status != 2 && list.status != 0)?.map((it) => ({
      label: `${it?.cycle} (${it?.batch_no})`,
      value: it?.cycle
    }));
    setCycleList(data)
  }, [V2VCycle])

  const references = [
    { label: "Fresh Apheresis", template: FreshIcon },
    { label: "Cryopreserved LSM", template: Cryopreserve },
    { label: "Pool Cycle", template: PoolIcon }
  ];

  const getSubjectDetails = async (id) => {
    try {
      const res = await bmrSubjectApi.getSubjectDetailsWithCryo(id, currentCycle?.feasibility_assessments_id);
      setSubjectData({ id: subject_id, phase_id: res?.data?.phase_id, feasibility_id: currentCycle?.feasibility_assessments_id, ...res?.data });
      dispatch(setSubjectDataList(res?.data))
    } catch (error) {
      console.log(error)
    }
  };

  useEffect(() => {
    const data = currentCycle?.data?.find((it) => it?.slug == 'product-manufactured')
    if (data?.status == '1' || data?.status == '4' || data?.status == '5') {
      setShowManufectureButton(true)
    }
  }, [currentCycle])

  useEffect(() => {
    if (subject_id)
      getSubjectDetails(subject_id);
  }, [subject_id]);

  // ─── Terminate CR detection ───────────────────────────────────────────────────
  // Check if any step has a CR with "change-terminate-cycle-status" in its fields
  const terminateCR = currentCycle?.data
    ?.flatMap(step => step?.change_requests || [])
    ?.find(cr =>
      cr?.fields?.some(field => field?.slug === TERMINATE_ACTION)
    );

  // CR has been raised if terminateCR exists (regardless of approval state)
  const isTerminateCRRaised = !!terminateCR;

  // ─── Terminate button rules ───────────────────────────────────────────────────
  // VISIBLE when:  user has RAISE permission  +  manufacturing reached  +  cycle active
  // DISABLED when: CR has already been raised for terminate
  const isCycleCompleted = currentCycle?.status == 2;
  const isCycleInactive = currentCycle?.status == 0;

  const hasTerminatePermission = hasCRPermission(cr_permissions, [TERMINATE_ACTION], "RAISE");

  const showTerminateButton =
    showManufectureButton &&
    hasTerminatePermission &&
    !isCycleCompleted &&
    !isCycleInactive;

  const isTerminateDisabled = isTerminateCRRaised; // disable once CR is raised
  // ─────────────────────────────────────────────────────────────────────────────

  const handleOpenModal = async (type) => {
    if (type == 'approve' || type == 'reject') {
      setLoadingButton(type); // Set loading for the button
      try {
        let res;
        const pendingCR = currentCycle?.data?.find(step => step?.cr_status === 'pending' && (step?.change_requests || step?.change_request));
        if (pendingCR) {
          const payload = {
            feasibility_assessment_id: currentCycle?.feasibility_assessments_id,
            product_manufacturing_stage_id: currentCycle?.product_manufacturing_id,
            patient_id: subject_id,
            qr_code_generated_id: currentCycle?.qr_code_generation_id,
          };

          if (type == 'approve') {
            res = await dispatch(approveChangeRequest({ data: payload })).unwrap();
          } else {
            res = await dispatch(rejectChangeRequest({ data: payload })).unwrap();
          }

          if (res?.status == 200) {
            const changeRequests = pendingCR?.change_requests || pendingCR?.change_request;
            const request_reason_id = changeRequests?.length > 0 ? changeRequests[changeRequests.length - 1]?.id : null;

            if (request_reason_id) {
              const approvalPayload = {
                request_reason_id: request_reason_id,
                qr_code_id: currentCycle?.qr_code_generation_id,
              };
              if (type === 'reject') {
                approvalPayload.confirm = true;
              }
              try {
                await bmrSubjectApi.requestApproval(approvalPayload);
              } catch (err) {
                console.log(err);
              }
            }
            if (onUpdate) onUpdate();
          }
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoadingButton(null); // Clear loading state
      }
    } else {
      setLoadingButton(type); // Set loading for the button
      setSlug(type);
      setOpenModal(true);
      setLoadingButton(null); // Clear loading state for modal buttons
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSlug('');
  };

  let currentActualSteps = [...(actualSteps || [])];
  if (!currentActualSteps.some(step => step.slug === "feasibility-assessment")) {
    currentActualSteps.push({ id: 33, name: "Feasibility Assessment", slug: "feasibility-assessment", type: 3, status: currentCycle?.status });
  }

  // Exclude "terminate" from the shared actionButtons loop — rendered separately
  const nonTerminateButtons = (actionButtons || []).filter(btn => btn.type !== 'terminate');

  return (
    <>
      <div className={`reference-section flex justify-between items-center pb-3 ${className}`}>
        <div className="flex items-center flex-wrap gap-2 min-w-0">
          <h4 className="text-sm font-semibold text-gray-700 whitespace-nowrap mb-2">Reference:</h4>
          <div className="flex gap-2 ml-2 mb-2 flex-wrap">
            {references.map((ref, index) => (
              <div key={index} className="flex items-center gap-2">
                {React.createElement(ref.template)}
                <span className="text-xs text-gray-600">{ref.label}</span>
                {index < references.length - 1 && (
                  <span className="text-gray-400 ml-2">|</span>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className='flex gap-3'>

          {/* ── Shared action buttons (approve / reject / change-request / pool-cycles) ── */}
          {showManufectureButton && nonTerminateButtons?.map((button, index) => {
            const pendingCR = currentCycle?.data?.find(step =>
              step?.cr_status === 'pending' &&
              (step?.change_requests || step?.change_request)?.some(cr =>
                cr?.fields?.some(field => field?.slug === 'change-terminate-cycle-status')
              )
            );
            console.log(currentCycle?.data, "currentCycle?.data", pendingCR)
            if (button.type === 'approve' || button.type === 'reject') {
              if (!pendingCR) return null;
              const canApprove = LoginData?.role_id == 1 || hasCRPermission(cr_permissions, [TERMINATE_ACTION], "RESOLVE");
              if (!canApprove) return null;
            }

            if (
              (!(button.type === 'approve' || button.type === 'reject') && !hasAuthority(button?.authority, bmr_permissions)) ||
              (button.type === 'change-request' && LoginData?.role_slug == 'superadmin')
            ) return null;

            const isDisabled =
              (button.type === 'change-request' && currentCycle?.change_request == 1) ||
              (button.type === 'pool-cycles' && cycleList?.length < 1) ||
              (button.type === 'pool-cycles' && currentCycle?.status == 2) ||
              (button.type === 'change-request' && currentCycle?.status != 2) ||
              currentCycle?.status == 0 ||
              loadingButton !== null; // Disable all buttons when any button is loading

            const isButtonLoading = loadingButton === button.type;

            return (
              <Tooltip
                title={getTooltipTextButton(
                  (button.type === 'change-request' && currentCycle?.change_request == 1),
                  (button.type === 'pool-cycles' && cycleList?.length < 1),
                  (button.type === 'pool-cycles' && currentCycle?.status == 2),
                  (button.type === 'change-request' && currentCycle?.status != 2),
                  currentCycle?.status == 0,
                  button?.label
                )}
                placement="top"
                key={'_tool_' + index}
              >
                <span key={'_btn_' + index}>
                  <Button
                    key={button.type}
                    type="button"
                    onClick={() => !isDisabled && handleOpenModal(button?.type)}
                    disabled={isDisabled}
                    variant={button.variant}
                    color={button.color}
                    className={isDisabled ? '' : button.className || ''}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 500,
                      borderRadius: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      ...(isDisabled && {
                        '&:hover': {
                          backgroundColor: 'inherit !important',
                          color: 'inherit !important',
                        }
                      }),
                      ...(!isDisabled && button.color === 'error' && {
                        '&:hover': { backgroundColor: '#009966' }
                      })
                    }}
                  >
                    {isButtonLoading && <CircularProgress size={16} color="inherit" />}
                    {button.label}
                  </Button>
                </span>
              </Tooltip>
            );
          })}

          {/* ── Separate Terminate button ─────────────────────────────────────────────
              Visible  → user has RAISE permission + manufacturing reached + cycle active
              Enabled  → no terminate CR raised yet
              Disabled → terminate CR already exists
          ─────────────────────────────────────────────────────────────────────────── */}
          {showTerminateButton && (
            <Tooltip
              title={isTerminateDisabled ? 'Terminate request already raised' : 'Terminate Cycle'}
              placement="top"
            >
              <span>
                <Button
                  type="button"
                  onClick={() => !isTerminateDisabled && handleOpenModal('terminate')}
                  disabled={isTerminateDisabled || loadingButton !== null}
                  variant="outlined"
                  color="error"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 500,
                    borderRadius: '6px',
                    backgroundColor: '#d32f2f',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    ...(isTerminateDisabled || loadingButton !== null && {
                      '&:hover': {
                        backgroundColor: 'inherit !important',
                        color: 'inherit !important',
                      }
                    }),
                    ...(!isTerminateDisabled && loadingButton === null && {
                      '&:hover': { backgroundColor: '#d32f2f', color: '#fff' }
                    })
                  }}
                >
                  {loadingButton === 'terminate' && <CircularProgress size={16} color="inherit" />}
                  Terminate
                </Button>
              </span>
            </Tooltip>
          )}

          {/* ── CR Raise ── */}
          {hasCRaisePermission(cr_permissions) && (
            <CR actualSteps={currentActualSteps} onUpdate={onUpdate} subject_id={subject_id} />
          )}

          {/* ── Request History ── */}
          {(hasAuthority(['start-manufacturing', 'manufacturing-approve'], bmr_permissions) || cr_permissions?.length > 0) && (
            <RequestHistory SubjectData={SubjectData} onUpdate={onUpdate} />
          )}
        </div>
      </div>

      {openModal && (
        <BsModal
          fullWidth={true}
          open={!!openModal}
          size='sm'
          onClose={handleCloseModal}
          title={
            slug === 'pool-cycles' ? 'Pool Cycles' :
              slug === 'terminate' ? 'Terminate Cycle' :
                'Change Request'
          }
        >
          <TemplateForm
            cycleList={cycleList}
            slug={slug}
            onUpdate={onUpdate}
            subject_id={subject_id}
            handleCloseModal={handleCloseModal}
          />
        </BsModal>
      )}
    </>
  );
};

export default ReferenceSection;